import { FunctionComponent, useState } from "react";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { classNames } from "@/utils/classNames";

import {
	CompanySegment,
	ContentStatus,
	SegmentContent,
	SegmentKey,
	SegmentStatusFilter,
} from "../schema/knowledgeDb";
import { getSegmentOverallStatus } from "../utils/transformers";

interface SegmentSidebarProps {
	companySegments: CompanySegment[];
	activeSegment: SegmentKey | null;
	lastModified: string;
	contents: SegmentContent[];
	statusFilter: SegmentStatusFilter;
	selectedSegmentIds: Set<number>;
	onSegmentSelect: (key: SegmentKey) => void;
	onStatusFilterChange: (filter: SegmentStatusFilter) => void;
	onToggleSegmentSelection: (contentId: number) => void;
	onSelectAll: (contentIds: number[]) => void;
	onBulkApprove: () => void;
}

export const SegmentSidebar: FunctionComponent<SegmentSidebarProps> = ({
	companySegments,
	activeSegment,
	lastModified,
	contents,
	statusFilter,
	selectedSegmentIds,
	onSegmentSelect,
	onStatusFilterChange,
	onToggleSegmentSelection,
	onSelectAll,
	onBulkApprove,
}) => {
	const [expandedCompanies, setExpandedCompanies] = useState<Set<number>>(
		() => new Set(companySegments.map((cs) => cs.id))
	);

	const toggleCompany = (companyId: number) => {
		setExpandedCompanies((prev) => {
			const next = new Set(prev);

			if (next.has(companyId)) {
				next.delete(companyId);
			} else {
				next.add(companyId);
			}

			return next;
		});
	};

	const isActive = (companyId: number, personaId: number) =>
		activeSegment?.scorePersonaCategoryId === personaId &&
		activeSegment?.scoreCompanyCategoryId === companyId;

	const formattedDate = new Date(lastModified).toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});

	/** Find content for a given persona + company pair */
	const getContentForSegment = (companyId: number, personaId: number) =>
		contents.find(
			(c) => c.scorePersonaCategoryId === personaId && c.scoreCompanyCategoryId === companyId
		);

	/** Check if a persona category should be visible given the current status filter */
	const isVisible = (companyId: number, personaId: number) => {
		if (statusFilter === SegmentStatusFilter.ALL) {
			return true;
		}

		const content = getContentForSegment(companyId, personaId);

		if (!content) {
			return statusFilter === SegmentStatusFilter.AI_DRAFTED;
		}

		const status = getSegmentOverallStatus(content);

		if (statusFilter === SegmentStatusFilter.AI_DRAFTED) {
			return status === ContentStatus.AI_DRAFTED || status === null;
		}

		return status === ContentStatus.HUMAN_APPROVED;
	};

	const isBulkMode = selectedSegmentIds.size > 0;

	/** Content ids for every persona currently shown under the active status filter. */
	const visibleContentIds = companySegments.flatMap((company) =>
		company.personas
			.filter((persona) => isVisible(company.id, persona.id))
			.map((persona) => getContentForSegment(company.id, persona.id)?.id)
			.filter((id): id is number => id !== undefined)
	);
	const allSelected =
		visibleContentIds.length > 0 && visibleContentIds.every((id) => selectedSegmentIds.has(id));
	const someSelected = visibleContentIds.some((id) => selectedSegmentIds.has(id));

	return (
		<div className="w-[280px] min-w-[280px] border-r border-gray-200 bg-brand-white flex flex-col sticky top-0 h-full overflow-y-auto">
			{/* Header */}
			<div className="px-4 py-3 border-b border-gray-200">
				<h3 className="text-sm font-semibold text-gray-900">Segments</h3>
				<p className="text-xs text-gray-500 mt-1">Last modified: {formattedDate}</p>
			</div>

			{/* Status Filter */}
			<div className="px-4 py-2 border-b border-gray-200">
				<select
					className="w-full text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
					value={statusFilter}
					onChange={(e) => onStatusFilterChange(e.target.value as SegmentStatusFilter)}
				>
					<option value={SegmentStatusFilter.ALL}>All Segments</option>
					<option value={SegmentStatusFilter.AI_DRAFTED}>AI-drafted only</option>
					<option value={SegmentStatusFilter.APPROVED}>Approved only</option>
				</select>
			</div>

			{/* Select all */}
			{visibleContentIds.length > 0 && (
				<div className="px-4 py-2 border-b border-gray-200">
					<Checkbox
						isChecked={allSelected}
						isIndeterminate={someSelected && !allSelected}
						onChange={() => onSelectAll(allSelected ? [] : visibleContentIds)}
					>
						<span className="text-xs font-medium text-gray-600">
							Select all ({visibleContentIds.length})
						</span>
					</Checkbox>
				</div>
			)}

			{/* Segment tree */}
			<div className="flex-1 overflow-y-auto py-2">
				{companySegments.map((company) => {
					const visiblePersonas = company.personas.filter((p) => isVisible(company.id, p.id));

					if (visiblePersonas.length === 0) {
						return null;
					}

					return (
						<div key={company.id} className="mb-1">
							{/* Company segment header */}
							<button
								className="flex items-center w-full px-4 py-2 text-left text-sm font-medium text-gray-900 hover:bg-gray-200/50 transition-colors"
								type="button"
								onClick={() => toggleCompany(company.id)}
							>
								<span
									className={classNames(
										"mr-2 text-xs text-gray-500 transition-transform",
										expandedCompanies.has(company.id) ? "rotate-90" : ""
									)}
								>
									▶
								</span>
								{company.name}
								<span className="ml-auto text-xs text-gray-400">{visiblePersonas.length}</span>
							</button>

							{/* Persona categories */}
							{expandedCompanies.has(company.id) && (
								<div className="ml-4">
									{visiblePersonas.map((persona) => {
										const content = getContentForSegment(company.id, persona.id);
										const segStatus = content ? getSegmentOverallStatus(content) : null;

										return (
											<div
												key={persona.id}
												className={classNames(
													"flex items-center w-full px-2 py-1.5 text-left text-sm transition-colors rounded-r-lg",
													isActive(company.id, persona.id)
														? "bg-brand-light/10 text-brand-light font-medium border-l-2 border-brand-light"
														: "text-gray-700 hover:bg-gray-200/50 border-l-2 border-transparent"
												)}
											>
												{/* Bulk selection checkbox */}
												{content && (
													<div className="mr-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
														<Checkbox
															isChecked={selectedSegmentIds.has(content.id)}
															onChange={() => onToggleSegmentSelection(content.id)}
														/>
													</div>
												)}
												<button
													className="flex-1 text-left truncate"
													type="button"
													onClick={() =>
														onSegmentSelect({
															scorePersonaCategoryId: persona.id,
															scoreCompanyCategoryId: company.id,
														})
													}
												>
													{persona.name}
												</button>
												{/* Status badge */}
												{segStatus === ContentStatus.AI_DRAFTED && (
													<span className="ml-1 flex-shrink-0 text-[10px]">✨</span>
												)}
												{segStatus === ContentStatus.HUMAN_APPROVED && (
													<span className="ml-1 flex-shrink-0 text-[10px] text-status-success">
														✓
													</span>
												)}
											</div>
										);
									})}
								</div>
							)}
						</div>
					);
				})}
			</div>

			{/* Bulk approve action bar */}
			{isBulkMode && (
				<div className="sticky bottom-0 px-4 py-3 border-t border-gray-200 bg-white">
					<button
						className="w-full px-3 py-2 text-xs font-medium text-white bg-status-warning rounded-lg hover:bg-status-warning/90 transition-colors"
						type="button"
						onClick={onBulkApprove}
					>
						✓ Approve Selected ({selectedSegmentIds.size})
					</button>
				</div>
			)}
		</div>
	);
};
